import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateRoleRequestSchema,
  GetRoleRequestSchema,
  ListRoleAccessRulesRequestSchema,
  ListRolesRequestSchema,
  RoleService,
  UpdateRoleRequestSchema,
} from '../gen/tenants/v1/tenants_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';
import type { AccessRuleInputData } from './useUserService';

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

/**
 * Hook to fetch one role by ID, for the role detail view. Visible to any
 * authenticated caller for a system/platform-wide role; tenant-scoped for a
 * custom role.
 *
 * @param roleId - Role to fetch
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with the role
 */
export function useGetRole(roleId: string, token?: string | null) {
  return useQuery({
    queryKey: ['roles', 'get', roleId, !!token],
    queryFn: async () => {
      const client = createClient(RoleService, transport(token));
      const request = create(GetRoleRequestSchema, { id: roleId });
      return await client.getRole(request);
    },
    enabled: !!roleId && !!token,
  });
}

export interface ListRolesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: 'name' | 'created_at';
  sortDir?: 'asc' | 'desc';
  scopeFilter?: 'system' | 'tenant' | 'platform' | '';
  resourceKindFilter?: string;
}

/**
 * Hook to list the roles available to a tenant: System roles (always
 * included), and, if tenantId is set, that tenant's own Custom roles. An
 * empty tenantId returns System roles only — used for the global
 * "System Roles" view. Server-side paginated.
 *
 * @param tenantId - Tenant to scope Custom roles to, or '' for System roles only
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param params - Pagination, search, sort, scope/resource-kind filters
 * @returns Query result with the list of roles
 */
export function useListRoles(tenantId: string, token?: string | null, params?: ListRolesParams) {
  return useQuery({
    queryKey: [
      'roles',
      'list',
      tenantId,
      !!token,
      params?.page ?? 1,
      params?.pageSize ?? 100,
      params?.search ?? '',
      params?.sortBy ?? 'name',
      params?.sortDir ?? 'asc',
      params?.scopeFilter ?? '',
      params?.resourceKindFilter ?? '',
    ],
    queryFn: async () => {
      const client = createClient(RoleService, transport(token));
      const request = create(ListRolesRequestSchema, {
        tenantId,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 100,
        search: params?.search ?? '',
        sortBy: params?.sortBy ?? 'name',
        sortDir: params?.sortDir ?? 'asc',
        scopeFilter: params?.scopeFilter ?? '',
        resourceKindFilter: params?.resourceKindFilter ?? '',
      });
      return await client.listRoles(request);
    },
    enabled: !!token,
  });
}

/**
 * Mutation hook to create a role. Pass tenantId for a tenant-scoped custom
 * role, or omit it for a platform-scope role (tenant-less Admin only — see
 * rbac.RequirePlatformAdmin). Pass grantsAll to make the role always hold
 * every permission, present and future — permissions is ignored server-side
 * when grantsAll is true. grantsAll never makes the role Admin: it stays
 * fully access_rules-scoped and editable. Optionally pass accessRules to
 * create the role's own initial access_rules in the same request/
 * transaction/audit event.
 * On success, invalidates the roles list cache and the created role's
 * access-rules cache.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for creating a role
 */
export function useCreateRole(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      tenantId?: string;
      name: string;
      description?: string;
      permissions: string[];
      grantsAll?: boolean;
      accessRules?: AccessRuleInputData[];
    }) => {
      const client = createClient(RoleService, transport(token));
      const request = create(CreateRoleRequestSchema, {
        tenantId: input.tenantId ?? '',
        name: input.name,
        description: input.description ?? '',
        permissions: input.permissions,
        grantsAll: input.grantsAll ?? false,
        accessRules: (input.accessRules ?? []).map((r) => ({
          ...r,
          effect: r.effect ?? 'allow',
        })),
      });
      return await client.createRole(request);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roles', 'list', variables.tenantId ?? ''] });
      queryClient.invalidateQueries({ queryKey: ['roles', 'list', ''] });
      if (data.role) {
        queryClient.invalidateQueries({ queryKey: ['roles', data.role.id, 'access-rules'] });
      }
    },
  });
}

/**
 * Mutation hook to edit a role's name/description/permissions/grantsAll,
 * and optionally its own access_rules sub-changes (rules to create and/or
 * existing rule IDs to revoke) — all in one request, applied server-side in
 * one transaction and recorded as one audit event, mirroring
 * useUpdateUserAccess. Rejected by the server outright if the target role is
 * Admin specifically — that role is permanently un-editable. Any other
 * role, grantsAll or not, stays editable, including toggling grantsAll.
 * On success, invalidates the roles list cache, the edited role's own
 * GetRole cache, and its access-rules cache.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for updating a role
 */
export function useUpdateRole(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      description?: string;
      permissions: string[];
      grantsAll?: boolean;
      rulesToCreate?: AccessRuleInputData[];
      ruleIdsToRevoke?: string[];
    }) => {
      const client = createClient(RoleService, transport(token));
      const request = create(UpdateRoleRequestSchema, {
        id: input.id,
        name: input.name,
        description: input.description ?? '',
        permissions: input.permissions,
        grantsAll: input.grantsAll ?? false,
        rulesToCreate: (input.rulesToCreate ?? []).map((r) => ({
          ...r,
          effect: r.effect ?? 'allow',
        })),
        ruleIdsToRevoke: input.ruleIdsToRevoke ?? [],
      });
      return await client.updateRole(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roles', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['roles', 'get', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['roles', variables.id, 'access-rules'] });
    },
  });
}

/**
 * Hook to list every access_rules row owned by one role — the role's own
 * envelope that every holder's user-level access_rules can only narrow
 * within, never widen (see accessrule.gateBoth).
 *
 * @param roleId - The role ID to list access rules for
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with the list of access rules
 */
export function useListRoleAccessRules(roleId: string, token?: string | null) {
  return useQuery({
    queryKey: ['roles', roleId, 'access-rules', !!token],
    queryFn: async () => {
      const client = createClient(RoleService, transport(token));
      const request = create(ListRoleAccessRulesRequestSchema, { roleId });
      return await client.listRoleAccessRules(request);
    },
    enabled: !!roleId && !!token,
  });
}
